import type { ChatContact, ChatContactWithChat, ChatOut } from '../types'
import { normalizeAvatarUrl, preferStableAvatar } from '../utils/userDataExtractor'
import {
  avatarDebugLog,
  eventsDebugLog,
  lastMessageDebugLog,
  summarizeAvatarUrlForLog,
  summarizeLastMessageForLog,
} from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import { isEventChatItem } from '@/modules/chat-matrix-fast/constants/chat'
import { ChatTransformer } from './chatTransformer'
import { isGroupLikeChatType, resolveChatAvatarFromIChat, resolveChatDisplayTitle } from '@/modules/chat-matrix-fast/constants/chat'
import type { IChat } from '@/modules/chat-matrix-fast/types/IChat'
import { isForgottenRoom } from '@/modules/chat-matrix-fast/services/matrixForgottenRooms'
import { isDialogChatType } from '@/modules/chat-matrix-fast/utils/dialogPresence'
import { resolveChatOnlineStatus } from '@/modules/chat-matrix-fast/utils/chatOnlineStatus'

export class ChatStateManager {
  static updateContactFromCoreChat(
    contact: ChatContactWithChat,
    coreChat: IChat,
    profileUserId?: string | number,
  ): ChatContactWithChat {
    const lm = coreChat.last_message
    const hasContent = Boolean(lm?.content && lm.content.trim())
    const hasAttachments = Boolean(lm?.attachments && Array.isArray(lm.attachments) && lm.attachments.length > 0)

    const existingLastMessage = contact.chat.lastMessage
    const existingHasContent = Boolean(existingLastMessage?.message
      && existingLastMessage.message !== 'Нет сообщений'
      && existingLastMessage.message.trim())
    const existingHasAttachments = Boolean(existingLastMessage?.attachments
      && Array.isArray(existingLastMessage.attachments)
      && existingLastMessage.attachments.length > 0)

    const corePreview = lm && (hasContent || hasAttachments)
      ? {
          message: lm.content || '',
          time: lm.created_at || '',
          senderId: lm.created_by?.id ? String(lm.created_by.id) : '0' as string | number,
          feedback: {
            isSent: true,
            isDelivered: true,
            isSeen: lm.is_read === true,
          },
          attachments: lm.attachments || [],
        }
      : null

    const existingTime = Date.parse(existingLastMessage?.time ?? '')
    const coreTime = Date.parse(lm?.created_at ?? '')
    // Only replace preview when core is strictly newer — avoids sidebar text flicker.
    const coreIsNewer = Boolean(
      corePreview
      && (
        !existingHasContent && !existingHasAttachments
        || Number.isNaN(existingTime)
        || (!Number.isNaN(coreTime) && coreTime > existingTime)
      ),
    )

    const lastMessage = coreIsNewer && corePreview
      ? corePreview
      : existingLastMessage && (existingHasContent || existingHasAttachments)
        ? existingLastMessage
        : corePreview ?? {
          message: 'Нет сообщений',
          time: '',
          senderId: 0 as string | number,
          feedback: { isSent: false, isDelivered: false, isSeen: false },
        }

    const previewSource = coreIsNewer
      ? 'core.last_message'
      : existingLastMessage && (existingHasContent || existingHasAttachments)
        ? 'existing.sidebar'
        : corePreview
          ? 'core.last_message'
          : 'fallback-empty'

    const previousText = existingLastMessage?.message ?? ''
    const nextText = lastMessage.message ?? ''
    if (previousText !== nextText) {
      if (isEventChatItem(coreChat)) {
        eventsDebugLog('sidebar · preview sync', {
          chatId: coreChat.id,
          source: previewSource,
          previous: previousText.slice(0, 80),
          next: nextText.slice(0, 80),
          core: summarizeLastMessageForLog(lm),
        })
      } else {
        lastMessageDebugLog('syncContacts · preview changed', {
          chatId: coreChat.id,
          source: previewSource,
          core: summarizeLastMessageForLog(lm),
          previous: summarizeLastMessageForLog({
            content: existingLastMessage?.message,
            created_at: existingLastMessage?.time,
            created_by: { id: existingLastMessage?.senderId },
          }),
          result: summarizeLastMessageForLog({
            content: lastMessage.message,
            created_at: lastMessage.time,
            created_by: { id: lastMessage.senderId },
          }),
        })
      }
    }

    const online = isDialogChatType(coreChat.type)
      ? resolveChatOnlineStatus(coreChat)
      : (contact.online || false)

    const isDialog = coreChat.type === 'dialog' || coreChat.type === 'direct'
    const resolvedIcon = normalizeAvatarUrl(resolveChatAvatarFromIChat(coreChat) || '')
    const previousIcon = ('icon' in contact.chat ? contact.chat.icon : undefined) || contact.avatar
    const stableIcon = preferStableAvatar(previousIcon, resolvedIcon)
    if (
      previousIcon
      && resolvedIcon
      && stableIcon
      && normalizeAvatarUrl(previousIcon) !== normalizeAvatarUrl(resolvedIcon)
      && normalizeAvatarUrl(stableIcon) === normalizeAvatarUrl(previousIcon)
    ) {
      avatarDebugLog('syncContact · kept previous avatar (suspicious sticky?)', {
        chatId: coreChat.id,
        title: coreChat.title,
        previous: summarizeAvatarUrlForLog(previousIcon),
        resolved: summarizeAvatarUrlForLog(resolvedIcon),
        stable: summarizeAvatarUrlForLog(stableIcon),
      })
    } else if (
      previousIcon
      && resolvedIcon
      && normalizeAvatarUrl(previousIcon) !== normalizeAvatarUrl(resolvedIcon)
    ) {
      avatarDebugLog('syncContact · avatar updated', {
        chatId: coreChat.id,
        title: coreChat.title,
        from: summarizeAvatarUrlForLog(previousIcon),
        to: summarizeAvatarUrlForLog(stableIcon || resolvedIcon),
      })
    }
    const displayTitle = resolveChatDisplayTitle(coreChat)
    const chatMetaFields = !isDialog && 'title' in contact.chat
      ? {
          title: displayTitle || contact.chat.title,
          description: coreChat.description ?? ('description' in contact.chat ? contact.chat.description : undefined),
          icon: stableIcon || ('icon' in contact.chat ? contact.chat.icon : undefined),
        }
      : {}

    const nameFields = displayTitle ? { fullName: displayTitle } : {}
    const avatarFields = stableIcon
      ? { avatar: stableIcon }
      : {}

    const updatedChat = {
      ...contact.chat,
      ...chatMetaFields,
      ...(isDialog
        ? {
            matrix_peer_id: coreChat.matrix_peer_id
              ?? ('matrix_peer_id' in contact.chat ? contact.chat.matrix_peer_id : undefined),
            matrix_room_id: coreChat.matrix_room_id
              ?? ('matrix_room_id' in contact.chat ? contact.chat.matrix_room_id : undefined),
          }
        : {}),
      unseenMsgs: coreChat.unread_count || 0,
      lastMessage,
    } as ChatOut

    return {
      ...contact,
      ...nameFields,
      ...avatarFields,
      online,
      matrixUserId: isDialog
        ? (coreChat.matrix_peer_id?.trim() || contact.matrixUserId)
        : contact.matrixUserId,
      chat: updatedChat,
    }
  }

  static syncContactsWithCore(
    contacts: ChatContactWithChat[],
    coreChats: IChat[],
    profileUserId?: string | number,
    activeChatId?: number | string,
  ): ChatContactWithChat[] {
    if (!coreChats.length) return contacts

    const coreChatMap = new Map<number | string, IChat>(
      coreChats.map(chat => [chat.id, chat]),
    )

    return contacts
      .filter(contact => {
        const chatId = contact.chat?.id ?? contact.id
        return coreChatMap.has(chatId)
      })
      .map(contact => {
        const coreChat = coreChatMap.get(contact.id)
        ?? (contact.chat?.id != null ? coreChatMap.get(contact.chat.id) : undefined)

        if (!coreChat) {
          return contact
        }

        const updatedContact = this.updateContactFromCoreChat(contact, coreChat, profileUserId)

        if (activeChatId && updatedContact.chat?.id === activeChatId) {
          return {
            ...updatedContact,
            chat: {
              ...updatedContact.chat,
              unseenMsgs: 0,
            },
          }
        }

        return updatedContact
      })
  }

  static findNewChats(
    existingContacts: ChatContactWithChat[],
    coreChats: IChat[],
  ): IChat[] {
    const existingIds = new Set(existingContacts.map(contact => String(contact.id)))

    return coreChats.filter(chat => {
      if (existingIds.has(String(chat.id))) {
        return false
      }

      // REST groups stay visible even if room is in localStorage «forgotten» (re-invite).
      if (isGroupLikeChatType(chat.type)) {
        return true
      }

      return !chat.matrix_room_id || !isForgottenRoom(chat.matrix_room_id)
    })
  }

  static enrichContactWithAvatar(
    contact: ChatContact,
    chat: IChat,
    profileUserId?: string | number,
  ): ChatContact {
    let avatar = resolveChatAvatarFromIChat(chat) || contact.avatar || ''

    if ((chat.type === 'dialog' || chat.type === 'direct') && chat.members?.length > 0) {
      const member = chat.members.find(m => {
        const memberId = m.user || m.user_uuid
        return memberId !== profileUserId
      })

      if (member) {
        const memberAvatar = member.avatar || member.photo || ''
        if (memberAvatar) {
          avatar = memberAvatar
        }
      }
    }
    else if (isGroupLikeChatType(chat.type) && chat.icon) {
      avatar = chat.icon
    }

    const normalizedAvatar = normalizeAvatarUrl(avatar)

    return {
      ...contact,
      avatar: normalizedAvatar || '',
    }
  }
}
